import { AnyaResponseDto } from "types/anya";

export const sha256Hash = async (input: string) => {
  const crypto = window.crypto;
  const encoder = new TextEncoder();
  const data = encoder.encode(input);
  const hash = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hash));
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return hashHex;
};

const STATUS = {
  IN_PROGRESS: "In Progress",
  CANCELLED: "CANCELLED",
  AWAITING_CONFIRMATION: "AWAITING_CONFIRMATION",
} as const;

const checkStatus = (
  response: AnyaResponseDto | null | undefined,
  status: string,
  caseSensitive = false,
): boolean => {
  if (!response?.status) return false;
  return caseSensitive
    ? response.status === status
    : response.status.toLowerCase() === status.toLowerCase();
};

export const isStatusInProgress = (
  response: AnyaResponseDto | null | undefined,
): boolean => checkStatus(response, STATUS.IN_PROGRESS);

export const isStatusNotInProgress = (
  response: AnyaResponseDto | null | undefined,
): boolean => !checkStatus(response, STATUS.IN_PROGRESS);

export const isStatusCancelled = (
  response: AnyaResponseDto | null | undefined,
): boolean => checkStatus(response, STATUS.CANCELLED);

export const isStatusAwaitingConfirmation = (
  response: AnyaResponseDto | null | undefined,
): boolean => checkStatus(response, STATUS.AWAITING_CONFIRMATION);

export const isStatusNotAwaitingConfirmation = (
  response: AnyaResponseDto | null | undefined,
): boolean => !checkStatus(response, STATUS.AWAITING_CONFIRMATION);

export const isResponseCompleted = (
  response: AnyaResponseDto | null | undefined,
): boolean =>
  isStatusNotInProgress(response) && isStatusNotAwaitingConfirmation(response);

export const isResponseReadyToDisplay = (
  response: AnyaResponseDto | null | undefined,
): boolean => isStatusCancelled(response) || isResponseCompleted(response);

export const shouldShowDetails = (
  response: AnyaResponseDto | null | undefined,
): boolean =>
  Boolean(
    response?.details && response?.status && isResponseCompleted(response),
  );

export const shouldShowReasoningSummary = (
  response: AnyaResponseDto | null | undefined,
  isCancelled: boolean,
): boolean =>
  Boolean(
    response?.reasoningSummary &&
      response.reasoningSummary !== "" &&
      (isCancelled || isResponseReadyToDisplay(response)),
  );

export const hasPollingTimedOut = (
  startTime: number | null,
  maxPollTime: number,
): boolean => {
  if (!startTime) return false;
  const elapsedTime = Date.now() - startTime;
  return elapsedTime >= maxPollTime;
};
